import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';

const DashboardTab = () => {
  const [meterCounts, setMeterCounts] = useState({
    totalMeters: 0,
    totalInstalled: 0,
    liveStatus: { on: 0, off: 0 },
    meterLiveStatus: { online: 0, offline: 0 },
  });
  const [monthlyUsageData, setMonthlyUsageData] = useState({ chartData: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [monthlyUsageLoading, setMonthlyUsageLoading] = useState(true);

  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchMeterCounts();
      fetchMonthlyUsageData();
    }
  }, [selectedFacilityId]);

  const fetchMeterCounts = async () => {
    try {
      setIsLoading(true);
      const response = await makeRequest2(
        `/api/app/power_meter_management/controllers/power_meters/facility_meters_count/${selectedFacilityId}`,
        'GET'
      );
      if (response && response.data?.data) {
        setMeterCounts(response.data.data);
      } else {
        toastify(response?.error || 'Failed to fetch meter counts', 'error');
      }
    } catch (error) {
      console.error('Error fetching meter counts:', error);
      toastify('An error occurred while fetching meter counts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMonthlyUsageData = async () => {
    try {
      setMonthlyUsageLoading(true);
      const response = await makeRequest2(
        `/api/app/power_meter_management/controllers/history_data/power_usage_monthly_graph/${selectedFacilityId}`,
        'GET'
      );
      if (response && response.success) {
        const usageData = response.data.data.usageData || {};
        const processedData = (usageData.raw || []).map(item => ({
          meter_sn: String(item.meter_sn),
          usage: Number(item.usage),
        }));
        setMonthlyUsageData({ chartData: processedData });
      } else {
        toastify(response?.error || 'Failed to fetch monthly usage data', 'error');
        setMonthlyUsageData({ chartData: [] });
      }
    } catch (error) {
      console.error('Error fetching monthly usage data:', error);
      toastify('An error occurred while fetching monthly usage data', 'error');
      setMonthlyUsageData({ chartData: [] });
    } finally {
      setMonthlyUsageLoading(false);
    }
  };

  const renderMonthlyUsageTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="custom-tooltip bg-white p-2 border rounded shadow">
        <p className="mb-1"><strong>Meter: {payload[0].payload.meter_sn}</strong></p>
        <p className="mb-0">Monthly Usage: <strong>{Number(payload[0].value).toFixed(6)} kWh</strong></p>
      </div>
    );
  };

  const CustomXAxisTick = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="end" fill="#666"
        transform="rotate(-45)" style={{ fontSize: '11px' }}>
        {payload.value}
      </text>
    </g>
  );

  if (!selectedFacilityId) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <i className="ti ti-building fa-2x text-warning" />
          <p className="mt-2">Please select a facility first</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active" role="tabpanel">

      {/* ── Top Stats ─────────────────────────────────────────────────────── */}
      <div className="row">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6>Overall Total Transactions:</h6>
              <h4>KES 0.00</h4>
              <small className="text-muted">Coming soon...</small>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6>Current Month Transactions:</h6>
              <h4>KES 0.00</h4>
              <small className="text-muted">Coming soon...</small>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h6 className="mb-3">Meters Overview</h6>
              {isLoading ? (
                <div className="text-center py-2">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
              ) : (
                <div className="row g-2 text-center">
                  <div className="col-6">
                    <div className="border rounded p-2">
                      <div className="fs-4 fw-bold">{meterCounts.totalMeters}</div>
                      <div className="text-muted small">Total</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="border rounded p-2">
                      <div className="fs-4 fw-bold">{meterCounts.totalInstalled}</div>
                      <div className="text-muted small">Installed</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="border rounded p-2" style={{ background: '#d1e7dd' }}>
                      <div className="fs-4 fw-bold text-success">{meterCounts.liveStatus.on}</div>
                      <div className="text-success small">ON</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="border rounded p-2" style={{ background: '#f8d7da' }}>
                      <div className="fs-4 fw-bold text-danger">{meterCounts.liveStatus.off}</div>
                      <div className="text-danger small">OFF</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="border rounded p-2" style={{ background: '#d1e7dd' }}>
                      <div className="fs-4 fw-bold text-success">{meterCounts.meterLiveStatus.online}</div>
                      <div className="text-success small">Online</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="border rounded p-2" style={{ background: '#f8d7da' }}>
                      <div className="fs-4 fw-bold text-danger">{meterCounts.meterLiveStatus.offline}</div>
                      <div className="text-danger small">Offline</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── PostPaid / Prepaid ────────────────────────────────────────────── */}
      <div className="row mt-3">
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="mb-3">PostPaid Maximum Amount Limit ( KES: 10,000 )</h6>
              <p className="text-muted small">All customers that have exceeded the maximum amount limit.</p>
              <div className="text-center py-3"><span className="text-muted">No data found.</span></div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="mb-3">Prepaid Minimum Amount Limit ( KES: 0 )</h6>
              <p className="text-muted small">All customers that have exceeded the minimum amount limit.</p>
              <div className="text-center py-3"><span className="text-muted">No data found.</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Monthly Usage Chart ───────────────────────────────────────────── */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Monthly Power Usage (kWh)</h5>
              <p className="text-muted">Monthly electricity usage for each meter in the facility</p>
              {monthlyUsageLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : monthlyUsageData.chartData.length > 0 ? (
                <div style={{ width: '100%', height: 500 }}>
                  <ResponsiveContainer>
                    <BarChart data={monthlyUsageData.chartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="meter_sn" tick={<CustomXAxisTick />} height={80} interval={0} />
                      <YAxis domain={[0, 'dataMax + 0.005']}
                        tickFormatter={(v) => v.toFixed(4)}
                        label={{ value: 'Usage (kWh)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip content={renderMonthlyUsageTooltip} />
                      <Legend />
                      <Bar dataKey="usage" name="Monthly Usage (kWh)" fill="#FF6B35" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="alert alert-info text-center">
                  <i className="ti ti-info-circle" />
                  <p className="mb-0">No monthly usage data available for this facility</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;